import { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { useLocation } from "react-router-dom"

import { LoadingPage, ErrorPage } from "src/components/loader"
import { Icon } from "src/components/icon"
import { SidebarLayout } from "src/components/sidebar-layout"
import {
  Sidebar,
  SidebarBody,
  SidebarFooter,
  SidebarHeader,
  SidebarSection,
  SidebarItem,
  SidebarLabel,
  SidebarSpacer,
} from "src/components/sidebar"
import { Navbar, NavbarItem, NavbarSection } from "src/components/navbar"
import { Checkbox } from "src/components/checkbox"
import { Text } from "src/components/text"
import { Button } from "src/components/button"

import { apiClient } from "src/utils"

import { setUser, setUserLoading, setUserError } from "src/store/auth.jsx"
import { setList, setLoading, setError, setRefresh } from "src/store/chat.jsx"
import {
  setCompanyList,
  setCompanyListLoading,
  setCompanyListError,
  setSectorList,
  setSectorListLoading,
  setSectorListError,
  setIndustryList,
  setIndustryListLoading,
  setIndustryListError,
} from "src/store/company.jsx"

export default function Layout({ children }) {
  const dispatch = useDispatch()
  const location = useLocation()

  const [showAllChats, setShowAllChats] = useState(false)

  const rootPath = location.pathname.split("/").pop()

  const user = useSelector((state) => state.auth.user)
  const userLoading = useSelector((state) => state.auth.userLoading)
  const userError = useSelector((state) => state.auth.userError)
  const companiesLoading = useSelector((state) => state.company.companyListLoading)
  const companiesError = useSelector((state) => state.company.companyListError)
  const sectorsLoading = useSelector((state) => state.company.sectorListLoading)
  const sectorsError = useSelector((state) => state.company.sectorListError)
  const industriesLoading = useSelector((state) => state.company.industryListLoading)
  const industriesError = useSelector((state) => state.company.industryListError)

  const chats = useSelector((state) => state.chat.list)
  const chatsLoading = useSelector((state) => state.chat.loading)
  const chatsError = useSelector((state) => state.chat.error)
  const refreshChats = useSelector((state) => state.chat.refresh)

  const getUser = () => {
    apiClient
      .get("/auth/user")
      .then((user) => {
        dispatch(setUser(user))
        dispatch(setUserError(false))
      })
      .catch(() => {
        dispatch(setUserError(true))
      })
      .finally(() => {
        dispatch(setUserLoading(false))
      })
  }

  const getCompanies = () => {
    dispatch(setCompanyListLoading(true))
    dispatch(setCompanyListError(false))
    apiClient
      .get("/company/list")
      .then((response) => dispatch(setCompanyList(response)))
      .catch(() => dispatch(setCompanyListError(true)))
      .finally(() => dispatch(setCompanyListLoading(false)))
  }

  const getSectors = () => {
    dispatch(setSectorListLoading(true))
    dispatch(setSectorListError(false))
    apiClient
      .get("/company/sectors")
      .then((response) => dispatch(setSectorList(response)))
      .catch(() => dispatch(setSectorListError(true)))
      .finally(() => dispatch(setSectorListLoading(false)))
  }

  const getIndustries = () => {
    dispatch(setIndustryListLoading(true))
    dispatch(setIndustryListError(false))
    apiClient
      .get("/company/industries")
      .then((response) => dispatch(setIndustryList(response)))
      .catch(() => dispatch(setIndustryListError(true)))
      .finally(() => dispatch(setIndustryListLoading(false)))
  }

  const getChats = () => {
    dispatch(setLoading(true))
    dispatch(setError(false))
    apiClient
      .get("/chat/list", {
        params: {
          show_all: showAllChats,
        },
      })
      .then((response) => {
        dispatch(setList(response))
        dispatch(setRefresh(false))
      })
      .catch(() => {
        dispatch(setError(true))
      })
      .finally(() => dispatch(setLoading(false)))
  }

  useEffect(() => {
    getUser()
    getCompanies()
    getSectors()
    getIndustries()
  }, [])

  useEffect(() => {
    if (user && !userLoading && refreshChats) {
      getChats()
    }
  }, [user, userLoading, refreshChats])

  if (userLoading || companiesLoading || sectorsLoading || industriesLoading) {
    return <LoadingPage />
  }

  if (userError || companiesError || sectorsError || industriesError) {
    return <ErrorPage />
  }

  return (
    <SidebarLayout
      navbar={
        <Navbar>
          <NavbarSection>
            <NavbarItem href={"/"}>
              <img src="/logo.png" className={"h-5"} />
              <p className="!text-base !font-light font-serif !text-olive-950 dark:!text-olive-100 h-fit my-auto">
                Plain Signal
              </p>
            </NavbarItem>
          </NavbarSection>
        </Navbar>
      }
      sidebar={
        <Sidebar className={`transition-all duration-300 w-full opacity-100`}>
          <SidebarHeader className="px-1 py-1">
            <SidebarSection className="max-lg:hidden">
              <SidebarItem key="logo" href={"/"} className="">
                <img src="/logo.png" className={"h-10 ml-1"} />
                <p className="!text-base !font-light font-serif !text-olive-950 dark:!text-olive-100 h-fit my-auto">
                  Plain Signal
                </p>
              </SidebarItem>
            </SidebarSection>
          </SidebarHeader>
          <SidebarBody>
            {!chatsLoading &&
              chats.length > 0 &&
              chats.map((chat) => (
                <SidebarItem key={chat.uuid} href={`/chat/${chat.uuid}`} current={rootPath === chat.uuid}>
                  <SidebarLabel className="">{chat.name}</SidebarLabel>
                </SidebarItem>
              ))}
            {!chatsLoading && chatsError && <SidebarItem disabled>Error loading chats</SidebarItem>}
            {!chatsLoading && chats.length === 0 && (
              <SidebarItem disabled className="hover:!cursor-default opacity-50 italic">
                No chats yet
              </SidebarItem>
            )}
            <SidebarSpacer />
            <SidebarSection>
              <Button
                plain
                onClick={() => {
                  setShowAllChats(!showAllChats)
                  dispatch(setRefresh(true))
                }}
                className="w-full flex flex-row items-center justify-start gap-2"
              >
                <Checkbox
                  checked={showAllChats}
                  onChange={() => {
                    setShowAllChats(!showAllChats)
                    dispatch(setRefresh(true))
                  }}
                />
                <Text>Show {showAllChats ? "only my" : "all PlainSignal"} chats</Text>
              </Button>
            </SidebarSection>
          </SidebarBody>
          <SidebarFooter>
            <SidebarSection>
              <SidebarItem
                onClick={() => {
                  localStorage.removeItem("token")
                  window.location.href = `https://auth-api.plainsignal.io/user_management/sessions/logout?session_id=${user.latest_session_id}`
                }}
              >
                <Icon icon={"ArrowRightStartOnRectangle"} variant={"solid"} />
                <SidebarLabel className="text-red-700">Sign out</SidebarLabel>
              </SidebarItem>
            </SidebarSection>
          </SidebarFooter>
        </Sidebar>
      }
    >
      {children}
    </SidebarLayout>
  )
}
